/**
 * Mobile app (React Native / iOS / Android) — call these against REACT_APP_API_URL / API base.
 * Do not call Taqnyat from the client; only these backend routes.
 */

const jsonHeaders = { "Content-Type": "application/json" };

function detailMessage(data, fallback) {
  const d = data && data.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join(" ");
  if (typeof d === "string") return d;
  return fallback;
}

/**
 * FIRST-TIME SETUP (native app) — patients created by staff who have not set a password yet.
 *
 * Use a dedicated "First-time setup" / "Activate account" flow (not the normal login form).
 *
 * Server config FIRST_PASSWORD_REQUIRED_FIELDS (default "username,mrn,email"):
 *   which fields must be sent and matched against the DB before SMS is sent.
 *   Examples: "username,mrn,email" | "username,mrn" | "username" | "mrn,email"
 *
 * Default (all three):
 * 1) Screen: username, MRN, email (must match records), and mobile number.
 * 2) sendFirstPasswordOtp(apiUrl, { phone, username, mrn, email }).
 * 3) verifyFirstPasswordOtp(apiUrl, flow_token, otp).
 * 4) setFirstPassword(apiUrl, setup_token, password).
 * 5) POST /auth/login/mobile with username + new password.
 *
 * OTP goes to the entered mobile; on success the backend stores that number on the patient.
 *
 * Returns { flow_token, message, expires_in_seconds }
 */
export async function sendFirstPasswordOtp(apiBaseUrl, body) {
  const res = await fetch(`${apiBaseUrl}/auth/mobile/first-password/send-otp`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "send-otp failed"));
  return data;
}

export async function verifyFirstPasswordOtp(apiBaseUrl, flowToken, otp) {
  const res = await fetch(`${apiBaseUrl}/auth/mobile/first-password/verify-otp`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ flow_token: flowToken, otp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "verify-otp failed"));
  return data;
}

export async function setFirstPassword(apiBaseUrl, setupToken, password) {
  const res = await fetch(`${apiBaseUrl}/auth/mobile/first-password/set-password`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ setup_token: setupToken, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "set-password failed"));
  return data;
}

export async function forgotPasswordVerifyAccount(apiBaseUrl, username, email) {
  const res = await fetch(`${apiBaseUrl}/auth/forgot-password/verify-account`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ username, email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "verify-account failed"));
  return data;
}

export async function forgotPasswordSendOtp(apiBaseUrl, flowToken, phone) {
  const res = await fetch(`${apiBaseUrl}/auth/forgot-password/send-otp`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ flow_token: flowToken, phone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "send-otp failed"));
  return data;
}

export async function forgotPasswordVerifyOtp(apiBaseUrl, flowToken, otp) {
  const res = await fetch(`${apiBaseUrl}/auth/forgot-password/verify-otp`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ flow_token: flowToken, otp }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "verify-otp failed"));
  return data;
}

export async function forgotPasswordSetPassword(apiBaseUrl, passwordResetToken, newPassword) {
  const res = await fetch(`${apiBaseUrl}/auth/forgot-password/set-password`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ password_reset_token: passwordResetToken, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "set-password failed"));
  return data;
}

export async function resetPasswordWithToken(apiBaseUrl, token, newPassword) {
  const res = await fetch(`${apiBaseUrl}/auth/reset-password`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "reset-password failed"));
  return data;
}

export async function resetPasswordWithOtp(apiBaseUrl, body) {
  const res = await fetch(`${apiBaseUrl}/auth/reset-password-otp`, {
    method: "POST",
    headers: jsonHeaders,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(detailMessage(data, res.statusText || "reset-password-otp failed"));
  return data;
}
